import '@calcom/atoms/globals.min.css';

import { useMemo } from 'react';
import { createPortal } from 'react-dom';

import { Booker } from '@calcom/atoms';
import { ModalElement } from '@shared/dream-components/src';

import type { CalComBooking } from '~/interfaces/cal_com';
import type { CalComFulfillmentMetadata } from '~/interfaces/fulfillment';

interface Props {
  eventSlug: string;
  calComUserName: string;
  currentSubscriberName?: string;
  currentSubscriberEmail?: string;
  onClose: () => void;
  onBookingSuccess: (bookingData: CalComBooking) => void;
  isOpen: boolean;
  shouldDisableConfirmButton?: boolean;
  fulfillmentMetadata?: CalComFulfillmentMetadata;
}

export const BookCoachingSessionModal = ({
  fulfillmentMetadata,
  eventSlug,
  calComUserName,
  currentSubscriberName,
  currentSubscriberEmail,
  onClose,
  onBookingSuccess,
  isOpen,
  shouldDisableConfirmButton = false,
}: Props) => {
  const handleBookingSuccess = (bookingData: { data: CalComBooking }) => {
    onBookingSuccess(bookingData.data);
  };

  const defaultFormValues = useMemo(() => {
    const values: Record<string, string | string[]> = {};

    if (fulfillmentMetadata?.cal_booking_fields_responses) {
      values.name = fulfillmentMetadata.cal_booking_fields_responses.name ?? '';
      values.email = fulfillmentMetadata.cal_booking_fields_responses.email;
    } else if (currentSubscriberEmail) {
      values.name = currentSubscriberName ?? '';
      values.email = currentSubscriberEmail;
    }

    return Object.keys(values).length ? values : undefined;
  }, [fulfillmentMetadata, currentSubscriberName, currentSubscriberEmail]);

  const bookerElement = (
    <Booker
      rescheduleUid={fulfillmentMetadata?.cal_booking_uid}
      eventSlug={eventSlug}
      username={calComUserName}
      onCreateBookingSuccess={handleBookingSuccess}
      defaultFormValues={defaultFormValues}
      hideBranding
      confirmButtonDisabled={shouldDisableConfirmButton}
    />
  );

  if (!isOpen) return null;

  return createPortal(
    <ModalElement
      isOpen={isOpen}
      onOpenChange={onClose}
      isLoading={shouldDisableConfirmButton}
      showCloseButton={false}
      hideOverflow={false}
      element={{
        type: 'modal',
        content: [],
      }}
    >
      {bookerElement}
    </ModalElement>,
    document.body,
  );
};
